import { Context, createContext, PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import { fetchAuthSession, signInWithRedirect, getCurrentUser, signOut } from 'aws-amplify/auth';
import { withAuthRegionFailover } from '@/auth';
import { useProfileQuery } from '@/queries/user';
import { getPermissionsList, PermissionType } from '@/permissions';
import { EmployeeRole } from '@/api/apis/resources';
import { wsDefaultConnector } from '@/ws';
import { Case } from '@/api/apis/case';
import { clearRedirectTarget, isEmployeeOnCase, REDIRECT_TO_PARAM, stashRedirectTarget } from '@/utils/helpers';
import { UserProfileType } from '@/api/apis/user';

type ProfileType = {
  userId: string;
  userName: string;
};

type AuthProviderProps = {
  isAuthLoading: boolean;
  isUserDataInitialized: boolean;
  isLoggedIn: boolean;
  profile: ProfileType | null;
  roles: EmployeeRole[];
  employeeData?: UserProfileType | null;
  checkPermission: (permission: PermissionType) => boolean;
  checkOwnCasePermission: (permission: PermissionType, caseData: Case) => boolean;
  signIn: (provider: string) => Promise<void>;
  getUserSession: () => Promise<void>;
  logout: () => Promise<void>;
};

export const initialAuthContextValues = {
  isAuthLoading: true,
  isUserDataInitialized: false,
  isLoggedIn: false,
  profile: null,
  employeeData: null,
  roles: [],
  checkPermission: () => true,
  checkOwnCasePermission: () => true,
  signIn: () => Promise.resolve(),
  getUserSession: () => Promise.resolve(),
  logout: () => Promise.resolve(),
};

export const AuthContext: Context<AuthProviderProps> = createContext<AuthProviderProps>(initialAuthContextValues);

export const AuthProvider = ({ children }: PropsWithChildren) => {
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [isLoggedIn, setLoggedIn] = useState(false);
  const [roles, setRoles] = useState<EmployeeRole[]>([]);
  const [permissions, setPermissions] = useState<PermissionType[]>([]);
  const [profile, setProfile] = useState<ProfileType | null>(null);
  const { data: employeeData } = useProfileQuery();

  const signIn = useCallback(async (provider: string) => {
    // Stash before SSO navigates away; AuthGuard restores it on return.
    stashRedirectTarget(new URLSearchParams(globalThis.location.search).get(REDIRECT_TO_PARAM));

    await signInWithRedirect({ provider: { custom: provider } });
  }, []);

  const getUserSession = useCallback(async () => {
    try {
      const authSession = await withAuthRegionFailover(() => fetchAuthSession());
      const accessToken = authSession.tokens?.accessToken.toString();

      setLoggedIn(!!accessToken);

      if (accessToken) {
        const currentUser = await getCurrentUser();

        localStorage.setItem('token', accessToken);
        localStorage.setItem('userId', currentUser.userId);

        setProfile({
          userId: currentUser.userId,
          userName: currentUser.username,
        });

        const currentRoles = (authSession.tokens?.accessToken.payload['cognito:groups'] ?? []) as EmployeeRole[];
        const permissionList = getPermissionsList(currentRoles);

        setPermissions(permissionList);
        setRoles(currentRoles);

        // Auth-driven redirects live in AuthGuard.
        if (permissionList.length === 0) {
          return;
        }

        await wsDefaultConnector.start();
      } else {
        setProfile(null);
        localStorage.removeItem('token');
        localStorage.removeItem('streamToken');
        localStorage.removeItem('streamChannelGrants');
        localStorage.removeItem('userId');
      }
    } finally {
      setIsAuthLoading(false);
    }
  }, []);

  useEffect(() => {
    getUserSession();
  }, [getUserSession]);

  const checkPermission = useCallback(
    (currentPermission: PermissionType) => permissions.includes(currentPermission),
    [permissions],
  );

  const checkOwnCasePermission = useCallback(
    (currentPermission: PermissionType, caseData: Case) =>
      !!employeeData?.employee?.id &&
      checkPermission(currentPermission) &&
      isEmployeeOnCase(employeeData.employee.id, caseData),
    [checkPermission, employeeData?.employee?.id],
  );

  const logout = useCallback(async () => {
    clearRedirectTarget();
    await signOut();
  }, []);

  const contextValue = useMemo(
    () => ({
      isAuthLoading,
      isUserDataInitialized: isLoggedIn && !!permissions.length,
      isLoggedIn,
      profile,
      roles,
      employeeData,
      signIn,
      getUserSession,
      checkPermission,
      checkOwnCasePermission,
      logout,
    }),
    [
      isAuthLoading,
      permissions,
      isLoggedIn,
      profile,
      roles,
      employeeData,
      getUserSession,
      checkPermission,
      checkOwnCasePermission,
      signIn,
      logout,
    ],
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};
